import { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContentText,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";


/** Extracts plain text from an HTML string without adding it to the DOM.
 * 
 * Creates an off-DOM container, sets its innerHTML, and returns textContent.
 * 
 * Useful for generating baseline text for TranslationGuard's expectedTexts. */
export function extractHTMLTextContent(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || '';
}

/**
 * A React component that monitors specific DOM selectors for text changes.
 * It compares each element’s textContent against a baseline and shows a visual warning and dialog when differences are detected.
 * Warnings are limited per tab using sessionStorage.
 * 
 * Use `extractHTMLTextContent` to generate expected text from HTML strings.
 * 
 * @param {Record<string, string>} expectedTexts A dict/object mapping CSS selectors to expected text content (textContent) for each element.
 */
export default function TranslationGuard(expectedTexts) {
  const intervalId = useRef(null);
  const alerted = useRef(false);
  const [shouldOpenDialog, setShouldOpenDialog] = useState(false);
  const [isTranslationDetected, setIsTranslationDetected] = useState(false);
  // Limit how many times we show this dialog per tab (uses sessionStorage)
  const STORAGE_KEY = 'translationGuard:shownCount';
  const MAX_SHOWS = 3;
  // Initialize to 0 on first render; we'll read sessionStorage lazily when a change is detected
  const shownCountRef = useRef(0);

  useEffect(() => {
    console.debug("TranslationGuard: starting monitoring of text changes:", JSON.stringify(expectedTexts));

    const checkExpectedTexts = () => {
      let anyTextChanged = false;
      const foundSelectors = []
      const changedSelectors = []

      for (const [selector, expectedText] of Object.entries(expectedTexts)) {
        const el = document.querySelector(selector);
        if (!el) continue;
        foundSelectors.push(selector);

        const currentText = el.textContent;
        const hasChanged = (currentText !== expectedText);

        if (hasChanged) {
          changedSelectors.push({ selector: selector, expected: expectedText, current: currentText });
        }

        anyTextChanged = anyTextChanged || hasChanged;
      }

      console.debug("TranslationGuard: checked selectors: ", foundSelectors, ", anyTextChanged: " + anyTextChanged, ", changed: ", changedSelectors);

      // Update the warning indicator state
      setIsTranslationDetected(anyTextChanged);

      if (anyTextChanged && !alerted.current) {
        // Read the current shown count from sessionStorage now (fresh per-check)
        try {
          if (typeof window !== 'undefined' && window.sessionStorage) {
            const stored = parseInt(window.sessionStorage.getItem(STORAGE_KEY) || '0', 10) || 0;
            shownCountRef.current = stored;
          }
        } catch (e) {
          console.warn('TranslationGuard: failed to read shown count from sessionStorage', e);
        }

        // Only open the dialog if we haven't exceeded the allowed number of shows
        if (shownCountRef.current < MAX_SHOWS) {
          setShouldOpenDialog(true);
          // increment and persist so future checks respect the per-tab limit
          shownCountRef.current += 1;
          try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
              window.sessionStorage.setItem(STORAGE_KEY, String(shownCountRef.current));
            }
          } catch (e) {
            console.warn('TranslationGuard: failed to write shown count to sessionStorage', e);
          }
        } else {
          console.debug('TranslationGuard: max shows reached, not opening dialog');
        }
      }
      alerted.current = anyTextChanged;
    };

    // Run first translation check after initial delay, then at steady intervals of steadyDelay:
    const initialDelay = 400;
    const steadyDelay = 5600;
    const timeoutId = setTimeout(() => {
      checkExpectedTexts();
      intervalId.current = setInterval(checkExpectedTexts, steadyDelay);
    }, initialDelay);

    // Remove inteval callback and timeout callback when `TranslationGuard` component is unmounted:
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId.current);
    }
  }, [expectedTexts]);

  return (
    <>
      {/* Warning indicator badge shown while translation is detected */}
      {isTranslationDetected && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            backgroundColor: 'rgba(255, 152, 0, 0.85)',
            color: '#fff',
            borderRadius: '8px',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            zIndex: 9998,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
          }}
          onClick={() => setShouldOpenDialog(true)}
          title="תרגום אוטומטי זוהה"
        >
          <TranslateIcon style={{ fontSize: '20px' }} />
          <span>תרגום זוהה</span>
        </div>
      )}
      <Dialog open={shouldOpenDialog}>
        <DialogTitle style={{ direction: "rtl", textAlign: "center" }}>
          שים לב!
        </DialogTitle>
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', backgroundColor: '#fff3e0', borderRadius: '50%' }}>
            <TranslateIcon style={{ fontSize: '36px', color: '#ff9800' }} />
          </div>
          <DialogContentText style={{ direction: "rtl", textAlign: "right" }} sx={{ padding: "0 16px" }}>
            נראה כי הטקסט בעמוד שונה מהטקסט המקורי. ייתכן וזה נובע מתרגום אוטומטי של הדפדפן. אנא וודאו שהדפדפן שלכם לא מתרגם את התוכן של העמודים באתר ובטלו כל פלאגין דפדפן שעלול לפגוע בתצוגת השאלות.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShouldOpenDialog(false)}>הבנתי</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

